import React from 'react';
import { Save, Loader2, History } from 'lucide-react';
import { formatMoney } from '../../utils/annotationHelpers';

// Lots table for the Annotation tool. Presentational only — state and handlers
// live in AnnotationPage.
const AnnotationLotsTable = ({
  loading,
  lots,
  drafts,
  saving,
  selectedDataset,
  currentUser,
  onDraftChange,
  onSaveLot,
  onOpenHistory,
  onSelectImage,
}) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left border-collapse">
      <thead>
        <tr className="bg-surface-2 border-b border-hairline">
          <th className="p-4 font-semibold text-muted w-24">Lot #</th>
          <th className="p-4 font-semibold text-muted w-28">Image</th>
          <th className="p-4 font-semibold text-muted w-32 text-right">Actual</th>
          <th className="p-4 font-semibold text-muted w-64">Actual Override</th>
        </tr>
      </thead>
      <tbody>
        {loading && (
          <tr>
            <td colSpan={4} className="px-3 py-6 text-center text-muted">
              <Loader2 size={16} className="inline animate-spin mr-2" />
              Loading lots…
            </td>
          </tr>
        )}
        {!loading && lots.length === 0 && (
          <tr>
            <td colSpan={4} className="px-3 py-6 text-center text-muted">
              {selectedDataset
                ? 'No lots found for this auction.'
                : 'Select an auction to see its lots.'}
            </td>
          </tr>
        )}
        {!loading && lots.map((lot) => {
          const lotNumber = String(lot.lot_number || '');
          const images = Array.isArray(lot.image_urls) ? lot.image_urls : [];
          const isSaving = !!saving[lotNumber];
          const draftValue = drafts[lotNumber] ?? '';
          const hasOverride =
            lot.refactored_actual !== null && lot.refactored_actual !== undefined;
          return (
            <tr key={lotNumber} className="hover:bg-surface-2 border-b border-hairline last:border-b-0">
              <td className="px-1.5 py-1 border-r border-hairline align-top font-mono text-ink">{lotNumber || '—'}</td>
              <td className="px-1.5 py-1 border-r border-hairline align-top">
                <div className="flex flex-wrap gap-1">
                  {images.map((url, uidx) => (
                    <img
                      key={uidx}
                      src={url}
                      alt={`Lot ${lotNumber} ${uidx + 1}`}
                      loading="lazy"
                      decoding="async"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectImage(url, images, lotNumber);
                      }}
                      className="w-10 h-10 object-cover rounded shadow-sm hover:scale-[1.5] hover:z-10 transition-transform cursor-pointer ring-2 ring-positive"
                    />
                  ))}
                  {images.length === 0 && (
                    <div className="w-10 h-10 bg-surface-2 rounded flex items-center justify-center text-[10px] text-muted">No Img</div>
                  )}
                </div>
              </td>
              <td className="p-4 align-top text-right font-semibold text-ink">
                {formatMoney(lot.actual_total)}
              </td>
              <td className="p-4 align-top">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={draftValue}
                    onChange={(e) => onDraftChange(lotNumber, e.target.value)}
                    placeholder={hasOverride ? '' : 'e.g. 1200.00'}
                    disabled={isSaving}
                    className="w-32 rounded border border-hairline bg-surface px-2 py-1 text-sm text-ink"
                  />
                  <button
                    type="button"
                    onClick={() => onSaveLot(lot)}
                    disabled={isSaving}
                    className="inline-flex items-center gap-1 rounded border border-positive/30 bg-positive/10 px-2 py-1 text-xs font-semibold text-positive hover:bg-positive/20 disabled:opacity-50"
                    title="Save actual override"
                  >
                    {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => onOpenHistory(lotNumber)}
                    className="inline-flex items-center gap-1 rounded border border-hairline bg-surface px-2 py-1 text-xs font-semibold text-ink hover:bg-surface-2"
                    title="View annotation history"
                  >
                    <History size={12} />
                    History
                  </button>
                </div>
                {hasOverride && (
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-muted">
                    <span>Saved: {formatMoney(lot.refactored_actual)}</span>
                    {lot.annotated_by_username
                      && lot.annotated_by_username !== currentUser?.username && (
                      <span
                        className="font-mono text-ink bg-surface-2 border border-hairline px-2 py-0.5 rounded"
                        title={`Annotated by ${lot.annotated_by_username}`}
                      >
                        By: {lot.annotated_by_username}
                      </span>
                    )}
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default AnnotationLotsTable;
